import { inject } from '@angular/core';
import { PostItem } from '@app/core/models';
import { Store } from '@ngrx/store';
import { ItemFn } from '../item-post/item-post.component';

import { Dialog } from '@angular/cdk/dialog';
import { ACADEMIC_YEAR_KEY } from '@app/config';
import { EclassWorkerEnum } from '@app/core/models/eclass-worker.enum';
import { AlertService, DailyService, NotificationService } from '@app/core/services';
import { helperTarget, ModalDailyTab } from '@app/modules/eclass/components/modal-daily/base';
import { ModalDailyComponent } from '@app/modules/eclass/components/modal-daily/modal-daily.component';
import { Checker, helperAcademicYear, notNull, safeDate, safeEmpty } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { AppState } from '@app/store/app.state';
import { first } from 'lodash';
import { combineLatest, of } from 'rxjs';
import { catchError, map, switchMap, take, tap } from 'rxjs/operators';
import { FilterService } from '../../services/filter.service';
import { Header } from '../../utils/postable';
import { ModalDuplicateDailyComponent } from '../modal-duplicate-daily/modal-duplicate-daily.component';
import { ModalViewDailyComponent } from '../modal-view-daily/modal-view-daily.component';
import { FilterControlEnum, FilterForm, StatusFilterTypeEnum } from '../posts-filter-bar/filter-bar';
import { ExtraDailyComponent } from './extra-daily.component';
import { SubtitleDailyComponent } from './subtitle-daily.component';

interface Service {
  store: Store;
  dialog: Dialog;
  alert: AlertService;
  dailyService: DailyService;
  notificationService: NotificationService;
  filterService: FilterService;
}
export const DailyFilter = (state: AppState) => ({
  status: [
    {
      key: StatusFilterTypeEnum.IsConcluded,
      type: StatusFilterTypeEnum.IsConcluded,
      value: undefined,
      description: 'geral.todos',
    },
    {
      key: `${StatusFilterTypeEnum.isPinned}#1`,
      type: StatusFilterTypeEnum.isPinned,
      value: 1,
      description: 'eclass.aula.fixado',
    },
  ],
  filters: filters(state),
});

export const DailyHeader: Header = {
  plural: 'eclass.agenda.filtros.aula.plural',
  singular: 'eclass.agenda.filtros.aula.singular',
  icon: 'icon-text',
  worker: EclassWorkerEnum.Daily,
};

export const DailyItem: ItemFn = () => {
  const services: Service = {
    store: inject(Store),
    dialog: inject(Dialog),
    alert: inject(AlertService),
    dailyService: inject(DailyService),
    notificationService: inject(NotificationService),
    filterService: inject(FilterService),
  };

  return {
    ...DailyHeader,
    color: 'border-modules-eclass-500 outline-modules-eclass-500',
    iconStyle: 'text-modules-eclass-700 bg-modules-eclass-500/10',
    labelStyle: 'border-modules-eclass-500 text-modules-eclass-700',
    subtitle: SubtitleDailyComponent,
    extra: ExtraDailyComponent,
    action: (item) => readOnly(item, services),
    options: (item) => options(item, services),
  };
};

function canEdit(item: PostItem, service: Service) {
  return combineLatest([
    service.store.select(AppSelectors.isAllow([20, 36])),
    service.store.select(AppSelectors.ActiveUser),
  ]).pipe(
    take(1),
    map(([allow, activeUser]) => ({
      allow,
      currentYear: helperAcademicYear(item.date, activeUser) === activeUser?.ano_letivo,
    })),
  );
}

function options(item: PostItem, service: Service) {
  return canEdit(item, service).pipe(
    map(({ allow, currentYear }) => {
      if (allow) {
        if (currentYear) {
          return [
            {
              icon: 'icon-edit',
              title: 'geral.editar',
              action: () => edit(item, service),
            },
            {
              icon: 'icon-eyes',
              title: 'geral.visualizar',
              action: () => readOnly(item, service, currentYear),
            },
            {
              icon: 'icon-duplicate-2',
              title: 'geral.duplicar',
              action: () => duplicate(item, service),
            },
            {
              icon: 'icon-trash-2',
              title: 'geral.remover',
              action: () => remove(item, service),
            },
          ];
        }
        return [
          {
            icon: 'icon-eyes',
            title: 'geral.visualizar',
            action: () => readOnly(item, service, currentYear),
          },
          {
            icon: 'icon-duplicate-2',
            title: 'geral.duplicar',
            action: () => duplicate(item, service),
          },
        ];
      }
      return [];
    }),
  );
}

function readOnly(item: PostItem, service: Service, currentYear = false) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.dailyService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    map((daily) => ({
      worker: EclassWorkerEnum.Daily,
      readOnly: !currentYear,
      element: daily,
      subtitle: SubtitleDailyComponent,
      item,
    })),
    switchMap((data) => service.dialog.open(ModalViewDailyComponent, { data, disableClose: true }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function duplicate(item: PostItem, service: Service) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);

  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.dailyService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    switchMap((data) => service.dialog.open(ModalDuplicateDailyComponent, { data }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function edit(item: PostItem, service: Service) {
  const activeUser$ = service.store.select(AppSelectors.ActiveUser);
  return activeUser$.pipe(
    notNull(),
    take(1),
    switchMap((activeUser) =>
      service.dailyService
        .show(item.reference_id, {}, { [ACADEMIC_YEAR_KEY.year]: helperAcademicYear(item.date, activeUser) })
        .pipe(safeEmpty()),
    ),
    map((daily) => ({
      current: ModalDailyTab.Daily,
      date: safeDate(item.date),
      entityId: Number(item.entity_id),
      groupId: first(item.targets)?.group_id,
      subjectId: first(item.targets)?.subject_id,
      data: { target: { ...helperTarget(daily), id: daily?.id }, data: daily },
      learningTrailsElements: daily?.trail_elements,
      games: daily?.games,
    })),
    switchMap((data) => service.dialog.open(ModalDailyComponent, { data, disableClose: true }).closed),
    tap((res) => {
      if (res) {
        service.filterService.refresh();
      }
    }),
  );
}

function remove(item: PostItem, service: Service) {
  return service.alert.confirm({ message: 'eclass.aula.confirmar-remover' }).pipe(
    notNull(),
    switchMap(() => service.dailyService.destroy(item.reference_id).pipe(catchError(() => of({ ret: 0 })))),
    tap((res) => {
      if (res.ret === 1) {
        service.notificationService.success('geral.sucesso');
        service.filterService.refresh();
      } else {
        service.notificationService.error('eclass.aula.erro-ao-excluir');
      }
    }),
  );
}

function filters(store: AppState): FilterForm {
  const filters = [FilterControlEnum.Group, FilterControlEnum.Subject, FilterControlEnum.Generic];
  if (Checker.isEmployee(store.funcao_padrao)) {
    filters.push(FilterControlEnum.Level, FilterControlEnum.Grade);
    if (!Checker.isTeacher(store.funcao_padrao)) {
      filters.push(FilterControlEnum.Author);
    }
  }
  return {
    fixed: [FilterControlEnum.Group, FilterControlEnum.Subject],
    filters,
  };
}
